/**
 * Auth Hooks — Client-side authentication via TanStack Query
 *
 * NO useEffect. All state management via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { STALE, clearPersistedCache } from './query-cache.js';
import type { PortalUser } from '@shared/types.js';

interface AuthState {
  user: PortalUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
}

// Check if we just came back from OAuth (server adds _auth=1 param)
// If so, force a refetch by using staleTime: 0 and clean up the URL
const justAuthenticated = (() => {
  const params = new URLSearchParams(window.location.search);
  if (params.has('_auth')) {
    params.delete('_auth');
    const qs = params.toString();
    // Keep the fragment: it is part of where sign-in returned to (LH-1508).
    const clean = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', clean);
    return true;
  }
  return false;
})();

/**
 * Hook to get current auth state.
 * Calls /auth/me — returns user if authenticated, null otherwise.
 */
export function useAuth(): AuthState {
  const { data, isLoading, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const res = await fetch('/auth/me', { credentials: 'include' });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error('Auth check failed');
      const body = await res.json() as { data: PortalUser };
      return body.data;
    },
    retry: false,
    staleTime: justAuthenticated ? 0 : STALE.AUTH,
  });

  return {
    user: data ?? null,
    isLoading,
    isAuthenticated: !!data,
    error: error as Error | null,
  };
}

/**
 * Redirect to login.
 */
export function login(redirect?: string): void {
  const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
  window.location.href = `/auth/login${params}`;
}

/**
 * Log out — clear session and refresh auth state.
 */
export function useLogout(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await api.post('/auth/logout');
    // Wipe cached Jira data (memory + localStorage) so the next person on this
    // browser never sees the previous user's issues before being redirected
    queryClient.clear();
    clearPersistedCache();
    window.location.href = '/login';
  };
}
